/**
 * Push Notification Service for Firebase Cloud Messaging (FCM)
 * 
 * This service handles:
 * - Requesting notification permissions
 * - Getting FCM device token
 * - Registering token with backend
 * - Handling foreground notifications
 */

import api from './api';
import { Capacitor } from '@capacitor/core';

// iter152 — Native platform detection so iOS + Android use the Capacitor
// PushNotifications plugin (backed by APNs on iOS, FCM on Android) while
// the web build continues to use Firebase JS SDK + service worker.
const isNativePlatform = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};
const nativePlatform = () => {
  try { return Capacitor.getPlatform(); } catch { return 'web'; }
};

// Firebase configuration from environment variables
const FIREBASE_CONFIG = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
};

// VAPID key for web push
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

let firebaseApp = null;
let messaging = null;
let initialized = false;

/**
 * Check if Firebase is configured
 */
export const isFirebaseConfigured = () => {
  return !!(
    FIREBASE_CONFIG.apiKey &&
    FIREBASE_CONFIG.projectId &&
    FIREBASE_CONFIG.messagingSenderId &&
    VAPID_KEY
  );
};

/**
 * Initialize Firebase if configured
 */
export const initializeFirebase = async () => {
  if (initialized) return !!messaging;
  initialized = true;

  if (!isFirebaseConfigured()) {
    console.log('Firebase not configured - push notifications disabled');
    return false;
  }

  try {
    // Dynamically import Firebase modules
    const { initializeApp, getApps, getApp } = await import('firebase/app');
    const { getMessaging, isSupported } = await import('firebase/messaging');

    // Check if messaging is supported in this browser
    const supported = await isSupported();
    if (!supported) {
      console.log('Firebase messaging not supported in this browser');
      return false;
    }

    // Initialize Firebase app (singleton)
    if (getApps().length === 0) {
      firebaseApp = initializeApp(FIREBASE_CONFIG);
    } else {
      firebaseApp = getApp();
    }

    // Get messaging instance
    messaging = getMessaging(firebaseApp);
    
    console.log('Firebase initialized successfully');
    return true;
  } catch (error) {
    console.error('Firebase initialization error:', error);
    return false;
  }
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return { granted: false, reason: 'not_supported' };
  }

  if (Notification.permission === 'granted') {
    return { granted: true, reason: 'already_granted' };
  }

  if (Notification.permission === 'denied') {
    return { granted: false, reason: 'denied' };
  }

  try {
    const permission = await Notification.requestPermission();
    return { 
      granted: permission === 'granted', 
      reason: permission 
    };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { granted: false, reason: 'error' };
  }
};

/**
 * Get FCM device token
 */
export const getFCMToken = async () => {
  if (!messaging) {
    const success = await initializeFirebase();
    if (!success) return null;
  }

  if (!messaging) {
    console.log('Messaging not available');
    return null;
  }

  try {
    const { getToken } = await import('firebase/messaging');
    
    // Register service worker if not already registered
    let swRegistration = null;
    if ('serviceWorker' in navigator) {
      try {
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service worker registered');
      } catch (swError) {
        console.warn('Service worker registration failed:', swError);
      }
    }

    const token = await getToken(messaging, { 
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration 
    });
    
    if (token) {
      console.log('FCM token obtained');
      return token;
    } else {
      console.log('No FCM token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * iter152 — Native (iOS/Android) push registration via Capacitor
 * PushNotifications plugin. On iOS this obtains an APNs token; on Android
 * an FCM registration token. Both are stored on the backend under the
 * same `fcm_token` field (backend already dispatches per platform).
 *
 * Returns { success, token, platform } or { success:false, reason }.
 */
const registerNativeDeviceToken = async (propertyId) => {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const perm = await PushNotifications.checkPermissions();
    let status = perm.receive;
    if (status !== 'granted') {
      const req = await PushNotifications.requestPermissions();
      status = req.receive;
    }
    if (status !== 'granted') {
      return { success: false, reason: 'permission_denied' };
    }

    // Register with OS push service (APNs / FCM).
    // We resolve the token via the plugin's 'registration' event.
    const token = await new Promise((resolve) => {
      let resolved = false;
      const timer = setTimeout(() => { if (!resolved) { resolved = true; resolve(null); } }, 15000);
      PushNotifications.addListener('registration', (t) => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timer);
        resolve(t?.value || null);
      });
      PushNotifications.addListener('registrationError', () => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timer);
        resolve(null);
      });
      PushNotifications.register();
    });
    if (!token) return { success: false, reason: 'no_token' };

    const platform = nativePlatform(); // 'ios' | 'android'
    await api.post('/push/register', {
      fcm_token: token,
      property_id: propertyId,
      device_info: {
        platform,
        capacitor: true,
        userAgent: navigator.userAgent,
        language: navigator.language,
        timestamp: new Date().toISOString(),
      },
    });
    return { success: true, token, platform };
  } catch (error) {
    console.error('[Push] native register failed:', error);
    return { success: false, reason: 'registration_failed', error };
  }
};

/**
 * Register device token with backend
 */
export const registerDeviceToken = async (propertyId) => {
  // iter152 — Native (iOS + Android): use Capacitor PushNotifications plugin.
  if (isNativePlatform()) return registerNativeDeviceToken(propertyId);

  try {
    // Request permission first
    const permissionResult = await requestNotificationPermission();
    if (!permissionResult.granted) {
      console.log('Notification permission not granted:', permissionResult.reason);
      return { success: false, reason: permissionResult.reason };
    }

    // Get FCM token
    const fcmToken = await getFCMToken();
    if (!fcmToken) {
      console.log('Could not get FCM token');
      return { success: false, reason: 'no_token' };
    }

    // Register with backend
    const response = await api.post('/push/register', {
      fcm_token: fcmToken,
      property_id: propertyId,
      device_info: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timestamp: new Date().toISOString()
      }
    });

    console.log('Device token registered with backend');
    return { success: true, token: fcmToken, response };
  } catch (error) {
    console.error('Error registering device token:', error);
    return { success: false, reason: 'registration_failed', error };
  }
};

/**
 * Unregister device token (on logout)
 */
export const unregisterDeviceToken = async () => {
  // iter152 — Native: ask the plugin what our current token is and unregister that.
  if (isNativePlatform()) {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      // Best-effort: we don't have a direct getToken() API; the backend can
      // clean up on logout by user_id. Still call the unregister endpoint so
      // any stored device_tokens row for this user is deactivated server-side.
      await api.post('/push/unregister', { fcm_token: null, platform: nativePlatform() });
      await PushNotifications.removeAllListeners();
      return { success: true };
    } catch (error) {
      console.error('[Push] native unregister failed:', error);
      return { success: false, error };
    }
  }
  try {
    const fcmToken = await getFCMToken();
    if (fcmToken) {
      await api.post('/push/unregister', { fcm_token: fcmToken });
      console.log('Device token unregistered');
    }
    return { success: true };
  } catch (error) {
    console.error('Error unregistering device token:', error);
    return { success: false, error };
  }
};

/**
 * Set up foreground message handler
 */
export const onForegroundMessage = async (callback) => {
  // iter152 — Native: subscribe via Capacitor PushNotifications events.
  if (isNativePlatform()) {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const recv = await PushNotifications.addListener('pushNotificationReceived', (n) => {
        if (callback) callback({
          notification: { title: n.title, body: n.body },
          data: n.data,
        });
      });
      const tap = await PushNotifications.addListener('pushNotificationActionPerformed', (n) => {
        if (callback) callback({
          notification: { title: n.notification?.title, body: n.notification?.body },
          data: n.notification?.data,
          actionId: n.actionId,
        });
      });
      return () => { recv.remove(); tap.remove(); };
    } catch (e) {
      console.error('[Push] native listener attach failed:', e);
      return null;
    }
  }

  if (!messaging) {
    const success = await initializeFirebase();
    if (!success) return null;
  }

  if (!messaging) return null;

  try {
    const { onMessage } = await import('firebase/messaging');
    
    return onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      
      // Show notification manually for foreground messages
      if (Notification.permission === 'granted') {
        const { title, body, icon } = payload.notification || {};
        const notificationTitle = title || 'SuperHost';
        const notificationOptions = {
          body: body || 'You have a new notification',
          icon: icon || '/logo192.png',
          badge: '/logo192.png',
          data: payload.data,
          tag: payload.data?.booking_id || 'superhost-notification',
          requireInteraction: true
        };

        // Show browser notification
        new Notification(notificationTitle, notificationOptions);
      }

      // Call the callback for in-app handling
      if (callback) {
        callback(payload);
      }
    });
  } catch (error) {
    console.error('Error setting up message handler:', error);
    return null;
  }
};

/**
 * Get push notification status from backend
 */
export const getPushStatus = async () => {
  try {
    const response = await api.get('/push/status');
    return response.RESULT || response;
  } catch (error) {
    console.error('Error getting push status:', error);
    return { initialized: false, available: false };
  }
};

/**
 * Get user notifications from backend
 */
export const getNotifications = async (limit = 50, unreadOnly = false) => {
  try {
    const response = await api.get('/push/notifications', {
      params: { limit, unread_only: unreadOnly }
    });
    return response.RESULT || [];
  } catch (error) {
    console.error('Error getting notifications:', error);
    return [];
  }
};

/**
 * Mark notifications as read
 */
export const markNotificationsRead = async (notificationId = null) => {
  try {
    await api.post('/push/notifications/read', { 
      notification_id: notificationId 
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking notifications read:', error);
    return { success: false, error };
  }
};

export default {
  isFirebaseConfigured,
  initializeFirebase,
  requestNotificationPermission,
  getFCMToken,
  registerDeviceToken,
  unregisterDeviceToken,
  onForegroundMessage,
  getPushStatus,
  getNotifications,
  markNotificationsRead
};
